import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { Button, Collapse } from 'reactstrap';
import { animateScroll as scroll } from 'react-scroll';

import { setUserSetting, updateUserSettings } from '../../../actions/Auth';
import { showModal } from '../../../actions';

import { sendTrackingEvent } from '../../../helpers';
import { getCurrentUser } from '../../../helpers/Auth';
import { getOnboardingSteps } from '../../../helpers/Onboarding';
import { getUserSetting } from '../../../helpers/Settings';
import { eventSubscribe, eventUnsubscribe } from '../../../helpers/Events';

import OnboardingInitialStep from './InitialStep';
import OnboardingCreateCollection from './CreateCollection';
import OnboardingAddTags from './AddTags';
import OnboardingIntegrate from './Integrate';
import OnboardingShare from './Share';
import OnboardingNeedHelp from './NeedHelp';
import OnboardingInviteTeam from './InviteTeam';
// import OnboardingPopularFeatures from './PopularFeatures';

const Onboarding = ({ modal, handleUserSetting, openFinishedModal }) => {
  const intl = useIntl();

  const [shouldShowFinishedModal, setShouldShowFinishedModal] = useState(false);

  const userSteps = getUserSetting('onboarding_steps');
  const onboardingSteps = getOnboardingSteps();
  const userStepsCompleted = userSteps ?
    userSteps.filter(s => s.completed && onboardingSteps.includes(s.slug)).map(s => s.slug)
    : [];

  const isOnboardingCompleted = userStepsCompleted.length === onboardingSteps.length;

  const onboardingOpen = getUserSetting('onboarding_open');
  const onboardingFinishedSetting = getUserSetting('onboarding_finished');
  const onboardingDismissed = getUserSetting('onboarding_dismissed');

  const [showOnboardingSteps, setShowOnboardingSteps] = useState(onboardingOpen);

  const handleShowOnboarding = async () => {
    const newValue = !showOnboardingSteps;
    handleUserSetting({ onboarding_open: newValue });
    setShowOnboardingSteps(newValue);

    sendTrackingEvent(`Onboarding checklist ${newValue ? 'showed' : 'hidden'}`);
  };

  const handleDismiss = () => {
    handleUserSetting({ onboarding_dismissed: true });

    sendTrackingEvent('Onboarding checklist dismissed');
  };

  useEffect(() => {
    if (!onboardingFinishedSetting &&
      onboardingSteps.length > 0 &&
      userStepsCompleted.length === onboardingSteps.length
      && !shouldShowFinishedModal
    ) {
      if (!modal) {
        openFinishedModal();
      } else {
        setShouldShowFinishedModal(true);
      }
    }
  }, [
    userSteps,
    openFinishedModal,
    onboardingFinishedSetting,
    onboardingSteps.length,
    userStepsCompleted.length,
    modal,
    shouldShowFinishedModal,
  ]);

  useEffect(() => {
    if (!modal && shouldShowFinishedModal && !onboardingFinishedSetting) {
      setShouldShowFinishedModal(false);
      openFinishedModal();
    }
  }, [modal, onboardingFinishedSetting, openFinishedModal, shouldShowFinishedModal]);

  useEffect(() => {
    const performedStepHandler = () => {
      if (userStepsCompleted.length === onboardingSteps.length) {
        scroll.scrollToTop({
          containerId: 'dashboard-container',
          duration: 300,
          smooth: true,
        });
        setShowOnboardingSteps(false);
      } else if (onboardingOpen) {
        setShowOnboardingSteps(onboardingOpen);
      }
    };

    eventSubscribe('performedOnboardingStep', performedStepHandler);

    return () => {
      eventUnsubscribe('performedOnboardingStep', performedStepHandler);
    };
  }, [onboardingOpen, onboardingSteps.length, userStepsCompleted.length]);

  if (onboardingSteps.length === 0 || onboardingDismissed) return null;

  return (
    <div className="onboarding">
      {/* eslint-disable jsx-a11y/click-events-have-key-events */}
      {/* eslint-disable jsx-a11y/no-static-element-interactions */}
      <div
        className={classnames('onboarding-section-title', { 'onboarding-section-title-box': !showOnboardingSteps })}
        onClick={() => {
          if (!showOnboardingSteps) {
            handleShowOnboarding();
          }
        }}
      >
        {/* eslint-enable jsx-a11y/click-events-have-key-events */}
        {/* eslint-enable jsx-a11y/no-static-element-interactions */}
        <div className="onboarding-section-title-left">
          <h3>
            {isOnboardingCompleted ?
              intl.formatMessage({ defaultMessage: 'Onboarding checklist complete!' }) :
              intl.formatMessage({ defaultMessage: 'Onboarding Checklist' })}
          </h3>
          <p>
            {isOnboardingCompleted ? (
              <>
                {intl.formatMessage({ defaultMessage: 'You can hide it for now and bring it back anytime from your dashboard personalization settings.' })}
              </>
            ) :
              intl.formatMessage({ defaultMessage: 'Get the most out of your Stockpress workspace by following these easy steps!' })}
          </p>
        </div>
        <div className="onboarding-section-title-right">
          {isOnboardingCompleted && (
            <Button
              className="dismiss"
              color="primary"
              size="sm"
              onClick={handleDismiss}
            >
              {intl.formatMessage({ defaultMessage: 'Dismiss' })}
            </Button>
          )}
          <div className="onboarding-progress">
            <div className="onboarding-progress-icons">
              {onboardingSteps.map(s => (
                <div
                  key={s}
                  className={classnames('onboarding-step-icon', { 'onboarding-step-icon-completed': userStepsCompleted.includes(s) })}
                >
                  <i className="fal fa-check onboarding-step-complete-icon" />
                  <i className="fal fa-xmark onboarding-step-incomplete-icon" />
                </div>
              ))}
            </div>
            <p>
              {userStepsCompleted.length} / {onboardingSteps.length}
              {' '}
              {intl.formatMessage({ defaultMessage: 'Completed' })}
            </p>
          </div>
          <Button
            color="link"
            onClick={handleShowOnboarding}
            size="sm"
          >
            {showOnboardingSteps && intl.formatMessage({ defaultMessage: 'Hide checklist' })}
            {!showOnboardingSteps && intl.formatMessage({ defaultMessage: 'Show checklist' })}
          </Button>
        </div>
      </div>
      <Collapse isOpen={showOnboardingSteps}>
        <div className="onboarding-section-content">
          <div className="onboarding-section-content-left">
            {onboardingSteps.map(slug => (
              <Fragment key={slug}>
                {slug === 'initial-step' && (
                  <OnboardingInitialStep slug="initial-step" />
                )}
                {slug === 'collection-created' && (
                  <OnboardingCreateCollection slug="collection-created" />
                )}
                {slug === 'tags-added' && (
                  <OnboardingAddTags slug="tags-added" />
                )}
                {slug === 'share-complete' && (
                  <OnboardingShare slug="share-complete" />
                )}
                {slug === 'user-invited' && (
                  <OnboardingInviteTeam slug="user-invited" />
                )}
              </Fragment>
            ))}
          </div>
          <div className="onboarding-section-content-right">
            <OnboardingNeedHelp />
            <OnboardingIntegrate />
            {/* <OnboardingPopularFeatures /> */}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  modal: state.App.modal,
});

const mapDispatchToProps = (dispatch) => ({
  handleUserSetting: (setting) => {
    const profile = getCurrentUser();
    if (profile) {
      dispatch(setUserSetting(setting));
      dispatch(updateUserSettings(profile.uuid, setting));
    }
  },
  openFinishedModal: () => {
    dispatch(showModal('OnboardingFinishedModal', {
      className: 'modal-onboarding-finished',
    }));
    sendTrackingEvent('Onboarding complete');
  },
});

Onboarding.propTypes = {
  handleUserSetting: PropTypes.func.isRequired,
  openFinishedModal: PropTypes.func.isRequired,
  modal: PropTypes.string,
};

Onboarding.defaultProps = {
  modal: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(Onboarding);
